import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload } from 'lucide-react';
import api from '../api/apiClient';

export default function UploadDocument() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('GENERAL');
  const [fileUrl, setFileUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (!title.trim() || !fileUrl.trim()) {
      setError('Title and document URL are required.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/documents', {
        title: title.trim(),
        category,
        fileUrl: fileUrl.trim(),
      });
      navigate('/documents');
    } catch (err: any) {
      setError(err.message || 'Failed to upload document');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="mx-auto max-w-2xl">
        <button
          type="button"
          onClick={() => navigate('/documents')}
          className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-950"
        >
          <ArrowLeft size={16} />
          Documents
        </button>

        <form onSubmit={submit} className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex items-center gap-3">
            <Upload className="h-6 w-6 text-forest-900" />
            <h1 className="text-2xl font-bold text-gray-950">Upload Document</h1>
          </div>

          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <label className="mb-4 block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Title</span>
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-forest-900"
              placeholder="Lease agreement"
            />
          </label>

          <label className="mb-4 block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Category</span>
            <select
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-forest-900"
            >
              <option value="GENERAL">General</option>
              <option value="LEASE">Lease</option>
              <option value="COMPLIANCE">Compliance</option>
              <option value="MAINTENANCE">Maintenance</option>
              <option value="RECEIPT">Receipt</option>
            </select>
          </label>

          <label className="mb-6 block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Document URL</span>
            <input
              value={fileUrl}
              onChange={(event) => setFileUrl(event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-forest-900"
              placeholder="https://..."
              type="url"
            />
          </label>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/documents')}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-forest-900 px-4 py-2 text-sm font-semibold text-white hover:bg-forest-800 disabled:opacity-60"
            >
              {saving ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
